import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Flashbar,
  FlashbarProps,
  Header,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
  TextContent,
} from "@amzn/awsui-components-react";
import {
  InvestigationEvent,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useInvestigation } from "../../hooks/useInvestigations";
import { GroupsFilterQuery, serializeGroupsFilter } from "./groupsFilter";
import {
  InvestigationWorkQueue,
  RegistrySection,
} from "./InvestigationWorkQueue";
import {
  InvestigationOverview,
  InvestigationSection,
} from "./InvestigationOverview";
import {
  InvestigationSuggestions,
  SuggestionStart,
} from "./InvestigationSuggestions";
import { InvestigationBacklog } from "./InvestigationBacklog";
import type { SuggestionSource } from "./InvestigationPopulationBuilder";
import { RowActionResolver } from "./RowActionResolver";
import { InvestigateRowForm } from "./InvestigateRowForm";
import { TransactionLookup } from "./TransactionLookup";
import { OpenInvestigationButton } from "./OpenInvestigationButton";
import { InvestigationPopulationBuilder } from "./InvestigationPopulationBuilder";
import { InvestigationStatusChip } from "./InvestigationStatusChip";
import { waitingOnCodeReview } from "./CodeReviewBlocker";
import { InvestigationStaleBadge } from "./InvestigationStaleBadge";
import { RedriveAttemptedBadge } from "./RedriveAttemptedBadge";
import { InvestigationAgentPrompt } from "./InvestigationAgentPrompt";
import { InvestigationRule } from "./InvestigationRule";
import { eventDetail, eventLabel } from "./InvestigationTimeline";
import {
  drawerViewKey,
  readSessionState,
  writeSessionState,
} from "./drawerSessionState";
import {
  CreatedSummary,
  LEGACY_SECTION_REDIRECTS,
  RowSeed,
  assignedSummary,
  backlogPagePath,
  caseRules,
  formatTimestamp,
  investigationPath,
  ruleToSeed,
  shownStatus,
} from "./investigationUtils";
import { NeedsOwnerBadge } from "./NeedsOwnerBadge";

export type DrawerRequest =
  | { kind: "overview" }
  /** One registry table: everyone's active cases, mine, the ownerless ones, or the resolved. */
  | { kind: "registry"; section: RegistrySection }
  | { kind: "suggestions" }
  | { kind: "backlog" }
  | { kind: "row"; seed: RowSeed }
  | { kind: "manual" }
  /** Run the transaction/order-ID lookup immediately (row visual without a dlq_pk column). */
  | { kind: "txn"; transactionId: string }
  | { kind: "investigation"; investigationId: string };

interface InvestigationDrawerProps {
  stage: Stage;
  /** External request (QuickSight callback, toolbar button). A new object triggers navigation. */
  request?: DrawerRequest;
  /** When set (standalone page), opening a case navigates instead of showing the quick view. */
  openInvestigation?: (investigationId: string) => void;
  /**
   * Fired when the top-level view changes, so a host page can mirror it into the URL. Views that
   * have no page of their own (a DLQ item, the builder, a quick view) report `undefined`.
   */
  onViewChange?: (
    section: InvestigationSection | undefined,
    filter?: GroupsFilterQuery,
    /** Which view reported it; the lookup section is both the form and a transaction lookup. */
    kind?: DrawerView["kind"]
  ) => void;
  /** The host renders "Investigate a DLQ item…" itself (page header), so the drawer does not. */
  hideItemLookup?: boolean;
  /** The host's page header already names the section, so the registry table doesn't repeat it. */
  pageTitled?: boolean;
  /** Passed to the registry table's filter bar (the Resolved | Cancelled switch). */
  registryFilterControl?: React.ReactNode;
  /**
   * Table filter the host carries in its URL (sidebar presets land here). It applies to whichever
   * filterable table is showing — the active registry or the backlog; see InvestigationTable /
   * InvestigationBacklog. A view the drawer opens with a filter of its own (the overview's "Needs
   * an owner" link, "Show mine") is reported through onViewChange so the host can adopt it.
   */
  filter?: GroupsFilterQuery;
  onFilterChange?: (filter: GroupsFilterQuery | undefined) => void;
  /**
   * Dashboard drawer mode: no work queue or backlog (they live on the full page); the item lookup
   * form is the home view and every Back leads there.
   */
  hideWorkQueue?: boolean;
}

export type DrawerViewKind = DrawerView["kind"];

type DrawerView =
  | { kind: "overview" }
  /** `filter`: a preset the view was opened with (e.g. Needs an owner from the overview). */
  | { kind: "registry"; section: RegistrySection; filter?: GroupsFilterQuery }
  | { kind: "suggestions" }
  /** The DLQ backlog: snapshot items no investigation covers, pre-grouped by queue and error. */
  | { kind: "backlog" }
  | { kind: "manual" }
  /** `back`: the view that led here (the transaction lookup); otherwise Back goes home. */
  | { kind: "row"; seed: RowSeed; back?: DrawerView }
  | { kind: "txn"; transactionId: string }
  | {
      kind: "builder";
      seed: RowSeed;
      recurrenceOf?: string;
      /** Set when the builder starts from a TrackStars suggestion (rule, title, comment, links). */
      fromSuggestion?: SuggestionSource;
      /** Where Cancel/Back returns to: the backlog group, the DLQ item, or (unset) home. */
      back?: DrawerView;
    }
  | { kind: "investigation"; investigationId: string };

/**
 * Investigations flow shared by the full page (work queue, backlog, lookups, population builder,
 * read-only preview) and the DLQ dashboard drawer (`hideWorkQueue`: item lookup and population
 * builder only — the queue is a full-page experience). Every mutating action (claim, transfer,
 * status, comments, conclusion, redrives) lives on an investigation's full page.
 */
const RECENT_ACTIVITY_LIMIT = 3;

/** The view an external request lands on; `home` for a plain "open the drawer" request. */
const viewForRequest = (
  request: DrawerRequest,
  home: DrawerView
): DrawerView => {
  switch (request.kind) {
    case "row":
      return { kind: "row", seed: request.seed };
    case "manual":
      return { kind: "manual" };
    case "backlog":
      return { kind: "backlog" };
    case "overview":
      return { kind: "overview" };
    case "registry":
      return { kind: "registry", section: request.section };
    case "suggestions":
      return { kind: "suggestions" };
    case "txn":
      return { kind: "txn", transactionId: request.transactionId };
    case "investigation":
      return {
        kind: "investigation",
        investigationId: request.investigationId,
      };
    default:
      return home;
  }
};

/** The side-navigation section a view belongs to; undefined for transient views. */
export const sectionOf = (
  view: DrawerView
): InvestigationSection | undefined => {
  switch (view.kind) {
    case "overview":
      return "overview";
    case "registry":
      return view.section;
    case "suggestions":
      return "suggestions";
    case "backlog":
      return "backlog";
    case "manual":
    case "txn":
      return "lookup";
    default:
      return undefined;
  }
};

/** The drawer view a side-navigation section opens. */
export const viewForSection = (section: InvestigationSection): DrawerView => {
  const legacy = LEGACY_SECTION_REDIRECTS[section];
  if (legacy) {
    // Retired address: the page it moved to, with the filter it implied.
    const target = viewForSection(legacy.to);
    return target.kind === "registry" && legacy.filter
      ? { ...target, filter: legacy.filter }
      : target;
  }
  switch (section) {
    case "active":
    case "resolved":
    case "cancelled":
    case "all":
      return { kind: "registry", section };
    case "suggestions":
      return { kind: "suggestions" };
    case "backlog":
      return { kind: "backlog" };
    case "lookup":
      return { kind: "manual" };
    default:
      return { kind: "overview" };
  }
};

interface InvestigationQuickViewProps {
  stage: Stage;
  investigationId: string;
}

const recentActivity = (events: InvestigationEvent[]) =>
  events.slice(0, RECENT_ACTIVITY_LIMIT).map((event) => {
    const detail = eventDetail(event);
    return (
      <li key={event.eventId ?? `${event.type ?? ""}-${event.timestamp ?? ""}`}>
        <strong>{eventLabel(event)}</strong>
        {detail ? ` — ${detail}` : ""}
        <Box variant="span" color="text-body-secondary" fontSize="body-s">
          {" "}
          · {event.actor ?? "system"} · {formatTimestamp(event.timestamp)}
        </Box>
      </li>
    );
  });

/** Data-only preview of one investigation; the primary action is opening the full page. */
export const InvestigationQuickView: React.FC<InvestigationQuickViewProps> = ({
  stage,
  investigationId,
}) => {
  const detail = useInvestigation(stage, investigationId);
  const investigation = detail.data?.investigation;

  if (detail.isLoading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner /> Loading investigation…
      </Box>
    );
  }
  if (detail.error || !investigation) {
    return (
      <Alert type="error" header="Could not load investigation">
        {detail.error?.message || "Not found"}
      </Alert>
    );
  }

  const fullPage = investigationPath(investigation.id, stage);
  const contributors = investigation.contributors || [];
  const events = detail.data?.timeline?.events || [];

  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        description="Read-only preview. Claim, update, comment and redrive from the full page."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <InvestigationAgentPrompt
              stage={stage}
              investigationId={investigation.id}
            />
            <Button
              variant="primary"
              href={fullPage}
              iconName="external"
              iconAlign="right"
              target="_blank"
            >
              Open full page
            </Button>
          </SpaceBetween>
        }
      >
        {investigation.title}
      </Header>
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <InvestigationStatusChip
          status={shownStatus(investigation)}
          documentRedrive={investigation.documentRedrive}
          waitingOn={waitingOnCodeReview(investigation)?.id}
        />
        <InvestigationStaleBadge investigation={investigation} />
        <Badge>{investigation.id}</Badge>
        {!!investigation.returnedCount && (
          <Badge color="red">Returned {investigation.returnedCount}</Badge>
        )}
        <RedriveAttemptedBadge investigation={investigation} />
        {investigation.assignment?.status === "PENDING" && (
          <Badge color="blue">Assigning items…</Badge>
        )}
      </SpaceBetween>
      <div>
        <Box variant="awsui-key-label">Population rule</Box>
        <InvestigationRule
          rules={caseRules(investigation)}
          categoryScope={investigation.categoryScope}
        />
      </div>
      <KeyValuePairs
        columns={2}
        items={[
          {
            label: "Owner",
            value: investigation.owner ? (
              <Badge color="blue">{investigation.owner}</Badge>
            ) : (
              <NeedsOwnerBadge />
            ),
          },
          {
            label: "Contributors",
            value: contributors.length ? (
              <SpaceBetween direction="horizontal" size="xxs">
                {contributors.map((c) => (
                  <Badge key={c}>{c}</Badge>
                ))}
              </SpaceBetween>
            ) : (
              "—"
            ),
          },
          {
            label: "Frozen snapshot",
            value: `${investigation.population?.count ?? 0} items`,
          },
          { label: "Items assigned", value: assignedSummary(investigation) },
          {
            label: "Redrives",
            value: `${investigation.redriveAttemptCount ?? 0} attempt(s)`,
          },
          { label: "Updated", value: formatTimestamp(investigation.updatedAt) },
          {
            label: "Evidence links",
            value: `${investigation.links?.length ?? 0}`,
          },
        ]}
      />
      {investigation.blocker?.reason && (
        <Alert type="error" header="Blocked">
          {investigation.blocker.reason}
          {investigation.blocker.nextReviewDate &&
            ` (next review ${formatTimestamp(
              investigation.blocker.nextReviewDate
            )})`}
        </Alert>
      )}
      {investigation.conclusion?.summary && (
        <Alert type="success" header="Conclusion">
          {investigation.conclusion.summary}
          {investigation.conclusion.rootCause && (
            <div>
              <strong>Root cause:</strong> {investigation.conclusion.rootCause}
            </div>
          )}
        </Alert>
      )}
      <TextContent>
        <h4>Recent activity</h4>
        {events.length ? (
          <ul>{recentActivity(events)}</ul>
        ) : (
          <p>No activity yet.</p>
        )}
        <p>
          <small>
            <a href={fullPage} target="_blank" rel="noreferrer">
              Open the full page
            </a>{" "}
            for the complete audit log, comments, conclusion and redrives.
          </small>
        </p>
      </TextContent>
    </SpaceBetween>
  );
};

export const InvestigationDrawer: React.FC<InvestigationDrawerProps> = ({
  stage,
  request,
  openInvestigation,
  onViewChange,
  hideItemLookup = false,
  pageTitled = false,
  registryFilterControl,
  hideWorkQueue = false,
  filter,
  onFilterChange,
}) => {
  const viewKey = drawerViewKey(stage, hideWorkQueue ? "dashboard" : "page");
  const home: DrawerView = hideWorkQueue
    ? { kind: "manual" }
    : { kind: "overview" };
  const homeLabel = hideWorkQueue ? "Item lookup" : "Overview";
  // Full-page-only views are not reachable when the queue is hidden.
  const fullPageOnly = new Set<DrawerView["kind"]>([
    "overview",
    "registry",
    "suggestions",
    "backlog",
  ]);
  const clamp = (next: DrawerView): DrawerView =>
    hideWorkQueue && fullPageOnly.has(next.kind) ? home : next;
  const [view, setView] = useState<DrawerView>(() =>
    clamp(
      (request && viewForRequest(request, home)) ??
        readSessionState<DrawerView>(viewKey) ??
        home
    )
  );
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Remember where the user is so Back from a full page (or reopening the drawer) resumes here.
  useEffect(() => {
    writeSessionState(viewKey, view);
  }, [viewKey, view]);
  const viewFilter = view.kind === "registry" ? view.filter : undefined;
  useEffect(() => {
    onViewChange?.(sectionOf(view), viewFilter, view.kind);
    // Only the section (and a preset it was opened with) matter to a host page; seeds/ids are
    // drawer-internal. The kind rides along so the host can tell the transaction lookup from the
    // bare form (both are the lookup section) without re-firing on every seed change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionOf(view), serializeGroupsFilter(viewFilter), view.kind === "txn"]);

  useEffect(() => {
    if (request) setView(clamp(viewForRequest(request, home)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const open = (investigationId: string) => {
    if (openInvestigation) openInvestigation(investigationId);
    else setView({ kind: "investigation", investigationId });
  };

  const notify = (type: FlashbarProps.Type, content: string) =>
    setFlash([
      {
        type,
        content,
        dismissible: true,
        id: `${Date.now()}`,
        onDismiss: () => setFlash([]),
      },
    ]);

  /** Shared by the builder and the resolver's one-click path. */
  const handleCreated = (
    id: string,
    summary: CreatedSummary,
    returnTo: DrawerView = home
  ) => {
    if (summary.duplicateOfInvestigationId) {
      // The claim won nothing: Lusca closed the new case at once and the work is on the owner.
      // Say so plainly and land on the owner, not on a closed empty case.
      notify(
        "warning",
        "Every item in this population is already owned by another active investigation, so the new case was closed as a duplicate. Opening the existing investigation."
      );
      setView(returnTo);
      open(summary.duplicateOfInvestigationId);
      return;
    }
    notify(
      summary.conflictedCount ? "warning" : "success",
      summary.assignmentPending
        ? "Investigation created. The population is large, so items are being claimed in the background; counts update shortly."
        : `Investigation created: ${summary.assignedCount} items assigned${
            summary.overlappingCount
              ? `, ${summary.overlappingCount} owned elsewhere`
              : ""
          }${
            summary.conflictedCount
              ? `, ${summary.conflictedCount} lost to a concurrent claim`
              : ""
          }.`
    );
    // The draft is done: don't resume into a builder for a population that now exists.
    // From the backlog, land back on it so the next group is one click away.
    setView(returnTo);
    // A transaction lookup is a checklist: stay on it (it re-runs and marks this group as
    // investigated) and offer the new case in a new tab instead of navigating away.
    if (returnTo.kind === "txn") {
      setFlash((current) =>
        current.map((f) => ({
          ...f,
          action: (
            <OpenInvestigationButton
              stage={stage}
              investigationId={id}
              variant="normal"
            >
              Open investigation
            </OpenInvestigationButton>
          ),
        }))
      );
      return;
    }
    open(id);
  };

  /**
   * Where to land once an investigation exists. From the backlog or a transaction lookup (via its
   * DLQ item) return there, so the next group is one click away — the lookup re-runs and marks the
   * group just created as investigated. Otherwise home.
   */
  const returnAfterCreate = (origin: DrawerView | undefined): DrawerView => {
    if (origin?.kind === "backlog" || origin?.kind === "suggestions")
      return clamp(origin);
    if (origin?.kind === "txn") return origin;
    if (origin?.kind === "row" && origin.back?.kind === "txn")
      return origin.back;
    return home;
  };

  /** Label for a one-step Back link, naming the view it leads to. */
  const backLabel = (target: DrawerView | undefined) => {
    switch ((target ?? home).kind) {
      case "txn":
        return "Back to lookup results";
      case "row":
        return "Back to DLQ item";
      case "backlog":
        return "Back to backlog";
      case "manual":
        return "Back to item lookup";
      case "registry":
      case "suggestions":
        return "Back to the list";
      default:
        return "Back to overview";
    }
  };
  const startFromSuggestion =
    (back: DrawerView) =>
    ({ suggestion, reportId, report }: SuggestionStart) =>
      setView({
        kind: "builder",
        seed: ruleToSeed(suggestion.derivedRule),
        fromSuggestion: { suggestion, reportId, report },
        back,
      });

  return (
    <SpaceBetween size="m">
      {flash.length > 0 && <Flashbar items={flash} />}
      {/* Section views are reached from the side navigation; only transient views need a way home. */}
      {view.kind !== home.kind &&
        (hideWorkQueue || sectionOf(view) === undefined) && (
          <Button
            variant="link"
            iconName="angle-left"
            onClick={() => setView(home)}
          >
            {homeLabel}
          </Button>
        )}
      {view.kind === "overview" && (
        <SpaceBetween size="m">
          {!hideItemLookup && (
            <Box>
              <Button
                iconName="search"
                onClick={() => setView({ kind: "manual" })}
              >
                Investigate a DLQ item…
              </Button>
            </Box>
          )}
          <InvestigationOverview
            stage={stage}
            onOpenInvestigation={open}
            onOpenSection={(section, sectionFilter) => {
              const next = viewForSection(section);
              setView(
                sectionFilter && next.kind === "registry"
                  ? { ...next, filter: sectionFilter }
                  : next
              );
            }}
            onStartFromGroup={(seed) =>
              setView({ kind: "builder", seed, back: { kind: "overview" } })
            }
            onStartFromSuggestion={startFromSuggestion({ kind: "overview" })}
          />
        </SpaceBetween>
      )}
      {view.kind === "registry" && (
        <InvestigationWorkQueue
          stage={stage}
          section={view.section}
          pageTitled={pageTitled}
          filterControl={registryFilterControl}
          onOpenInvestigation={open}
          // Hosted on the page the URL owns the filter; standalone, the view it was opened with
          // seeds the table and edits stay table-local.
          filter={onFilterChange ? filter : view.filter}
          onFilterChange={
            onFilterChange ?? ((next) => setView({ ...view, filter: next }))
          }
        />
      )}
      {view.kind === "suggestions" && (
        <InvestigationSuggestions
          stage={stage}
          onStart={startFromSuggestion({ kind: "suggestions" })}
        />
      )}
      {view.kind === "backlog" && (
        <InvestigationBacklog
          stage={stage}
          // In the dashboard drawer offer the shareable full page; on it already, nothing to link to.
          fullPageHref={
            openInvestigation ? undefined : backlogPagePath(stage, filter)
          }
          filter={filter}
          onFilterChange={onFilterChange}
          onStart={(seed) =>
            setView({ kind: "builder", seed, back: { kind: "backlog" } })
          }
          onStartFromSuggestion={startFromSuggestion({ kind: "backlog" })}
        />
      )}
      {view.kind === "manual" && (
        <InvestigateRowForm
          onSubmit={(submission) =>
            setView(
              submission.kind === "txn"
                ? { kind: "txn", transactionId: submission.transactionId }
                : { kind: "row", seed: submission.seed }
            )
          }
          // The form is the home view in the dashboard drawer: nothing to cancel back to.
          onCancel={
            hideWorkQueue ? undefined : () => setView({ kind: "overview" })
          }
        />
      )}
      {view.kind === "txn" && (
        <TransactionLookup
          stage={stage}
          transactionId={view.transactionId}
          onSelect={(seed) => setView({ kind: "row", seed, back: view })}
          onBack={() => setView({ kind: "manual" })}
        />
      )}
      {view.kind === "row" && (
        <RowActionResolver
          stage={stage}
          seed={view.seed}
          onStart={(seed, recurrenceOf) =>
            // Back from the builder returns to this item, with the live lookup already resolved.
            setView({ kind: "builder", seed, recurrenceOf, back: view })
          }
          onBack={() => setView(view.back ?? home)}
          backLabel={backLabel(view.back)}
          onCreated={(id, summary) =>
            handleCreated(id, summary, returnAfterCreate(view))
          }
        />
      )}
      {view.kind === "builder" && (
        <InvestigationPopulationBuilder
          stage={stage}
          seed={view.seed}
          recurrenceOfInvestigationId={view.recurrenceOf}
          fromSuggestion={view.fromSuggestion}
          onCreated={(id, summary) =>
            handleCreated(id, summary, returnAfterCreate(view.back))
          }
          onCancel={() => setView(view.back ?? home)}
          onOpenExisting={open}
          // Seeded from a DLQ item: a back step that keeps the draft, not a discard.
          backLabel={
            view.back?.kind === "row" ? backLabel(view.back) : undefined
          }
        />
      )}
      {view.kind === "investigation" && (
        <InvestigationQuickView
          stage={stage}
          investigationId={view.investigationId}
        />
      )}
    </SpaceBetween>
  );
};

export default InvestigationDrawer;
